import {
  Box,
  Divider,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import BusinessIcon from "@mui/icons-material/BusinessOutlined";
import LocationIcon from "@mui/icons-material/LocationOnOutlined";
import ProductIcon from "@mui/icons-material/Inventory2Outlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ClearIcon from "@mui/icons-material/CloseRounded";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useScope } from "../context/ScopeContext";
import { useAuth } from "../auth/AuthContext";
import { isCustomerSession } from "../auth/audience";
import {
  OVERVIEW_PATHS,
  effectiveOverviewProduct,
  overviewProductsFor,
  type OverviewProduct,
} from "../nav/customerOverview";
import { PREMISE_SCOPED_PREFIXES } from "../nav/products";
import { PRODUCT_LABELS, PRODUCT_ORDER } from "../nav/products";

interface Option {
  value: string;
  label: string;
}

/**
 * One header selector: an icon, a two-line label (what it filters / what is
 * currently picked) and a chevron. Reads as a field, behaves as a menu.
 */
function ScopeSelect({
  icon,
  label,
  value,
  options,
  onChange,
  testId,
  allActionLabel,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  options: Option[];
  onChange: (value: string) => void;
  testId: string;
  /** Wording for the clear-the-filter row at the bottom of the menu. The
   *  resting label stays "All locations" - "Show all locations" is an action,
   *  and reads wrong as a description of the current state. */
  allActionLabel: string;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [query, setQuery] = useState("");
  const current = options.find((o) => o.value === value) ?? options[0];
  // "Filter aktivt" in the design system: a filter that is narrowing something
  // must not look identical to one that is not.
  const active = !!value;

  const choices = options.filter((o) => o.value !== "");
  const allOption = options.find((o) => o.value === "");
  // "många alternativ (alltid sök)" vs "få alternativ (0-10) - ingen sök".
  const searchable = choices.length > 10;
  const shown = searchable
    ? choices.filter((o) =>
        o.label.toLowerCase().includes(query.trim().toLowerCase())
      )
    : choices;

  function close() {
    setAnchor(null);
    setQuery("");
  }

  return (
    <>
      <Box
        role="button"
        tabIndex={0}
        aria-haspopup="listbox"
        aria-label={label}
        data-testid={testId}
        data-active={active ? "true" : "false"}
        onClick={(e) => setAnchor(e.currentTarget)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setAnchor(e.currentTarget);
          }
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          minWidth: { xs: 0, sm: 180 },
          maxWidth: 215,
          flex: { xs: 1, sm: "0 0 auto" },
          pl: 2,
          // The clear button brings its own padding - keeping pr: 2 as well
          // pushes the label into a squeeze it does not need.
          pr: active ? 1 : 2,
          py: 1,
          borderRadius: 2,
          border: "1px solid",
          borderColor: active ? "primary.main" : "divider",
          bgcolor: active ? "action.selected" : "background.paper",
          cursor: "pointer",
          userSelect: "none",
          transition: "border-color 120ms ease, background-color 120ms ease",
          "&:hover": { bgcolor: "action.hover" },
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: 2,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            color: active ? "primary.main" : "text.secondary",
            "& svg": { fontSize: 20 },
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 500,
              lineHeight: "14px",
              color: "text.secondary",
            }}
          >
            {label}
          </Typography>
          <Typography
            noWrap
            sx={{
              fontSize: 12,
              fontWeight: 600,
              lineHeight: "16px",
              color: active ? "primary.main" : "text.primary",
            }}
          >
            {current?.label ?? "-"}
          </Typography>
        </Box>
        {/* Clearing a filter is a one-click job, not a hunt through the menu
            for the row at the bottom. It only exists while something IS
            filtered - an X on an empty filter says the wrong thing. */}
        {/* No "all" row, no X: a selector that must always hold a value
            (a customer's product switch) cannot be cleared. */}
        {active && allOption && (
          <Tooltip title={allActionLabel}>
            <IconButton
              aria-label={allActionLabel}
              data-testid={`${testId}-clear`}
              onClick={(e) => {
                // The field itself opens the menu; the X must not.
                e.stopPropagation();
                onChange("");
              }}
              onKeyDown={(e) => {
                // Same for the keyboard: without this, Enter on the X clears
                // AND reopens the menu it just answered.
                if (e.key === "Enter" || e.key === " ") e.stopPropagation();
              }}
              sx={{
                p: 0.375,
                flexShrink: 0,
                color: "primary.main",
                "&:hover": { bgcolor: "action.selected" },
                "& svg": { fontSize: 16 },
              }}
            >
              <ClearIcon />
            </IconButton>
          </Tooltip>
        )}
        <KeyboardArrowDownIcon
          sx={{
            fontSize: 16,
            color: active ? "primary.main" : "text.secondary",
            flexShrink: 0,
          }}
        />
      </Box>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        slotProps={{ paper: { sx: { maxHeight: 420, minWidth: 240 } } }}
      >
        {searchable && (
          <Box sx={{ px: 1.5, pt: 0.5, pb: 1 }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              placeholder={`Search ${label.toLowerCase()}…`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              inputProps={{ "data-testid": `${testId}-search` }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}
        {shown.map((o) => (
          <MenuItem
            key={o.value}
            selected={o.value === value}
            onClick={() => {
              onChange(o.value);
              close();
            }}
            data-testid={`${testId}-option-${o.value}`}
          >
            {o.label}
          </MenuItem>
        ))}
        {shown.length === 0 && (
          <MenuItem disabled>No match for &ldquo;{query}&rdquo;</MenuItem>
        )}
        {allOption && <Divider />}
        {allOption && (
          <MenuItem
            selected={value === ""}
            onClick={() => {
              onChange("");
              close();
            }}
            data-testid={`${testId}-option-all`}
            sx={{ color: "primary.main", fontWeight: 600 }}
          >
            {allActionLabel}
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

/**
 * The app-wide scope selectors that live in the header: customer, location and
 * screen type. Every fleet-wide page reads the same selection, so narrowing
 * once narrows everywhere.
 */
export default function ScopeBar() {
  const { scope, setScope, companies, premises, premisesInScope, loaded } =
    useScope();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const customer = isCustomerSession(user);

  // A customer's product selector switches their ONE Overview between the
  // two products that have one (see nav/customerOverview.ts). Staff keep the
  // ordinary filter over every product.
  const overviewProducts = useMemo(
    () => (customer ? overviewProductsFor(premises) : []),
    [customer, premises]
  );
  const overviewProduct = effectiveOverviewProduct(
    scope.product,
    overviewProducts
  );
  // The selector always holds a value for a customer: settle a stored
  // "All products" (or a product they no longer have) onto the effective one.
  useEffect(() => {
    if (!customer || !loaded || !overviewProduct) return;
    if (scope.product !== overviewProduct) {
      setScope({ product: overviewProduct });
    }
  }, [customer, loaded, overviewProduct, scope.product, setScope]);

  function pickOverviewProduct(product: string) {
    const next = product as OverviewProduct;
    const patch: { product: string; premiseId?: string } = { product: next };
    // The Frontlayer overview is ONE location's dashboard. With a single
    // place that has Frontlayer there is nothing to choose - open it.
    if (next === "frontlayer" && !scope.premiseId) {
      const places = premises.filter((p) =>
        overviewProductsFor([p]).includes("frontlayer")
      );
      if (places.length === 1) patch.premiseId = places[0]!.premiseId;
    }
    setScope(patch);
    navigate(OVERVIEW_PATHS[next]);
  }

  /**
   * Narrowing the scope is also a navigation: picking a customer means "show me
   * their locations", picking a location means "take me into it". Widening is
   * not - clearing a selector leaves you on the page you were reading, unless
   * that page needs the premise you just cleared.
   */
  function pickCustomer(companyId: string) {
    // Choosing a customer means "show me their locations", which is the one
    // thing you cannot see while standing inside one of them. Clear it.
    setScope({ companyId, premiseId: "" });
    if (companyId) navigate("/premises");
  }

  function pickLocation(premiseId: string) {
    setScope({ premiseId });
    if (premiseId) {
      // A customer on the Signage overview stays on it, narrowed to the
      // place; everywhere else a location opens its dashboard.
      if (!(customer && location.pathname.startsWith(OVERVIEW_PATHS.signage))) {
        navigate("/places/overview");
      }
      return;
    }
    if (PREMISE_SCOPED_PREFIXES.some((p) => location.pathname.startsWith(p))) {
      navigate("/premises");
    }
  }

  const companyOptions = useMemo<Option[]>(
    () => [
      { value: "", label: "All customers" },
      ...companies.map((c) => ({ value: c.id, label: c.name })),
    ],
    [companies]
  );

  const premiseOptions = useMemo<Option[]>(
    () => [
      { value: "", label: "All locations" },
      ...premisesInScope.map((p) => ({
        value: p.premiseId,
        label: p.premiseName,
      })),
    ],
    [premisesInScope]
  );

  // Products, not screen types. Which places are in play is an estate
  // question; which device family is a device-table one (see the Platform
  // filter on the Screens/Fleet tables).
  const productOptions = useMemo<Option[]>(
    () => [
      { value: "", label: "All products" },
      ...PRODUCT_ORDER.map((k) => ({ value: k, label: PRODUCT_LABELS[k] })),
    ],
    []
  );

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: { xs: 1, md: 2 },
        flexWrap: "wrap",
        flexShrink: 0,
      }}
      data-testid="scope-bar"
    >
      {/* Only Nenda staff pick a customer. A customer's own users - company
          admin or premise admin - ARE the customer (Sigge, 2026-09-29). */}
      {!customer && companies.length > 0 && (
        <ScopeSelect
          icon={<BusinessIcon />}
          label="Customer"
          value={scope.companyId}
          options={companyOptions}
          onChange={pickCustomer}
          testId="scope-customer"
          allActionLabel="Show all customers"
        />
      )}
      <ScopeSelect
        icon={<LocationIcon />}
        label="Location"
        value={scope.premiseId}
        options={premiseOptions}
        onChange={pickLocation}
        testId="scope-location"
        allActionLabel="Show all locations"
      />
      {customer ? (
        overviewProducts.length > 0 && (
          <ScopeSelect
            icon={<ProductIcon />}
            label="Product"
            value={overviewProduct ?? ""}
            options={overviewProducts.map((k) => ({
              value: k,
              label: PRODUCT_LABELS[k],
            }))}
            onChange={pickOverviewProduct}
            testId="scope-product"
            allActionLabel="Show all products"
          />
        )
      ) : (
        <ScopeSelect
          icon={<ProductIcon />}
          label="Product"
          value={scope.product}
          options={productOptions}
          onChange={(product) => setScope({ product })}
          testId="scope-product"
          allActionLabel="Show all products"
        />
      )}
    </Box>
  );
}
